import { getUserSession, isEqualObject } from 'ui-smartforce-settings';
import {
  AnalyticsConfig,
  AnalyticsFiltersRequest,
  AnalyticsPeriodOptions,
  ClusterMapWidget,
  FilterMapDataType,
  FilterMapWidget,
  FilterMapWidgetConfig,
  HeatMapWidget,
  IncidentData,
  MapBaseDataType,
  MapWidget,
  PatternMapWidget,
  Widget,
  WidgetBase,
  WidgetColor,
  WidgetFilterOption
} from '../Models';
import {
  WIDGET_FILTER_MAP,
  WIDGET_HEAT_MAP,
  WIDGET_CLUSTER_MAP,
  WIDGET_RATE_CARD,
  WIDGET_TYPES,
  WIDGET_STACKED_LINE,
  WIDGET_GROUPED_BAR
} from '../Constants/Analytics';
import { apiGet, apiPost } from '../Helpers';
import {
  ANALYTICS_DAY_PALETTE,
  ANALYTICS_NIGHT_PALETTE
} from '../Constants/Colors';
import { APP_API_BASE_URL } from '../Constants/Url';

const PATTER_MAP_LIMIT = 300;
const LOCAL_STORAGE_KEY = 'Smartforce.CitizenContact.WidgetsFavourites';

function getWidgetWithData(
  config: AnalyticsConfig,
  id: number,
  position: number
): Widget {
  const widget: Widget = config.widgets.find(
    (widget: Widget) => widget.id === id
  ) as Widget;

  widget.position = position;

  if (widget.component.name.indexOf('_map') === -1) {
    if (
      widget.component.name === WIDGET_STACKED_LINE ||
      widget.component.name === WIDGET_GROUPED_BAR
    ) {
      widget.component.config.data = widget.component.config.data.map(
        (d, index) => ({
          ...d,
          color: {
            day:
              d.color && typeof d.color === 'string'
                ? d.color
                : ANALYTICS_DAY_PALETTE[index % ANALYTICS_DAY_PALETTE.length],
            night:
              d.color && typeof d.color === 'string'
                ? d.color
                : ANALYTICS_NIGHT_PALETTE[
                    index % ANALYTICS_NIGHT_PALETTE.length
                  ]
          }
        })
      );
    }
    return widget;
  } else {
    const mapWidget: MapWidget = widget.component as MapWidget;
    const filter = mapWidget.config.filter;

    let data: IncidentData[] = config.incident_data;

    if (filter) {
      data = data.filter((value: IncidentData) => {
        if (!value[filter.key]) {
          return false;
        }

        const dataValue = value[filter.key];

        // Check if it's the special 'not null' filter
        if (filter.value === 'not null') {
          return dataValue !== null && dataValue !== undefined;
        } else {
          // Check if data is an array: string[]
          if (Array.isArray(dataValue)) {
            return dataValue.includes(filter.value);
          } else {
            return dataValue === filter.value;
          }
        }
      });
    }

    if (widget.component.name === WIDGET_FILTER_MAP) {
      const componentConfig = widget.component.config as FilterMapWidgetConfig;
      const optionKey: string = componentConfig.options_key;
      let colorDict: Record<string, WidgetColor> = {};
      let filterMapOptions: WidgetFilterOption[] = [];

      componentConfig.options.forEach((option, index) => {
        const optionColor = {
          day:
            componentConfig.colors && componentConfig.colors.length > 0
              ? componentConfig.colors[index]
              : ANALYTICS_DAY_PALETTE[index % ANALYTICS_DAY_PALETTE.length],
          night:
            componentConfig.colors && componentConfig.colors.length > 0
              ? componentConfig.colors[index]
              : ANALYTICS_NIGHT_PALETTE[index % ANALYTICS_NIGHT_PALETTE.length]
        };

        colorDict[option] = optionColor;
        filterMapOptions = [
          ...filterMapOptions,
          {
            label: option,
            color: optionColor
          }
        ];
      });

      let mapData: FilterMapDataType[] = [];

      for (let incident of data) {
        if (incident[optionKey]) {
          let values: string[] = [];
          if (Array.isArray(incident[optionKey])) {
            values = incident[optionKey] as string[];
          } else {
            values = [incident[optionKey] as string];
          }

          for (let value of values) {
            mapData.push({
              coords: incident.coords,
              incident_number: incident.incident_number,
              incident_date: incident.incident_date,
              author_name: incident.author_name,
              name: value,
              color: colorDict[value]
            });
          }
        }
      }

      mapWidget.config.mapData = mapData;
      widget.component.config.filterMapOptions = filterMapOptions;

      return { ...widget, component: mapWidget as FilterMapWidget };
    } else {
      const mapData: MapBaseDataType[] = data.map((incident: IncidentData) => ({
        coords: incident.coords,
        incident_date: incident.incident_date,
        incident_number: incident.incident_number,
        author_name: incident.author_name
      }));

      mapWidget.config.mapData = mapData;

      if (widget.component.name === WIDGET_HEAT_MAP) {
        return { ...widget, component: mapWidget as HeatMapWidget };
      } else if (widget.component.name === WIDGET_CLUSTER_MAP) {
        return { ...widget, component: mapWidget as ClusterMapWidget };
      } else {
        // It's a 'pattern_map'
        const limited = mapWidget.config.mapData.length > PATTER_MAP_LIMIT;
        // Limit pattern map data to avoid render issues
        mapWidget.config.mapData = mapWidget.config.mapData.slice(
          -PATTER_MAP_LIMIT
        );
        return {
          ...widget,
          component: { ...(mapWidget as PatternMapWidget), limited }
        };
      }
    }
  }
}

class AnalyticsServiceClass {
  private config: AnalyticsConfig | undefined;
  private widgetListLite: WidgetBase[] = [];
  private filters: AnalyticsFiltersRequest | undefined;

  getConfig() {
    return this.config;
  }

  getWidgetListLite() {
    return this.widgetListLite;
  }

  async updateConfig(
    filters?: AnalyticsFiltersRequest
  ): Promise<AnalyticsConfig> {
    if (this.config && isEqualObject(filters, this.filters)) {
      return Promise.resolve(this.config);
    } else {
      const url = `${APP_API_BASE_URL}/analytics/generate`;
      const period = filters?.period || 'month';

      const fetchResp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `bearer ${getUserSession().access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(
          filters
            ? {
                period: filters.period,
                period_value: filters.period_value,
                filters:
                  Object.keys(filters.filters).length > 0
                    ? filters.filters
                    : undefined
              }
            : { period }
        )
      });

      if (fetchResp.ok) {
        return fetchResp.json().then((config: AnalyticsConfig) => {
          let widgets: Widget[] = [];
          this.widgetListLite = [];

          config.widgets
            .filter((w) => WIDGET_TYPES.includes(w.component.name))
            .forEach((w: Widget, index: number) => {
              widgets = [...widgets, getWidgetWithData(config, w.id, index)];

              this.widgetListLite = [
                ...this.widgetListLite,
                {
                  id: w.id,
                  title: w.title,
                  type: w.component.name
                }
              ];
            });

          this.config = { ...config, period, widgets };
          this.filters = filters;

          return this.config;
        });
      } else {
        const body = await fetchResp.json();
        return Promise.reject({
          code: fetchResp.status,
          text: fetchResp.statusText,
          detail: body.detail
        });
      }
    }
  }

  getWidget(id: number): Widget | undefined {
    return this.config
      ? (this.config.widgets.find((w: Widget) => w.id === id) as Widget)
      : undefined;
  }

  getWidgets(ids: number[]): Widget[] {
    if (!this.config) return [];
    else {
      return ids
        .map((id: number) => this.getWidget(id))
        .filter((w) => !!w) as Widget[];
    }
  }

  getNewWidgets(): WidgetBase[] {
    if (!this.config) return [];

    const removeSuggestions = this.widgetListLite.filter(
      (w) =>
        !this.config?.suggested_widgets.find((value: Number) => w.id === value)
    );

    const newRateCards = removeSuggestions.filter(
      (w) => w.type === WIDGET_RATE_CARD
    );

    let newWidgets = newRateCards.slice(newRateCards.length - 5);

    const newOthers = removeSuggestions.filter(
      (w) => w.type !== WIDGET_RATE_CARD
    );

    newWidgets = [...newWidgets, ...newOthers.slice(newOthers.length - 5)];

    return newWidgets;
  }

  getSuggestions(): WidgetBase[] {
    let result: WidgetBase[] = [];
    if (this.config?.suggested_widgets) {
      for (let id of this.config.suggested_widgets) {
        const widget = this.widgetListLite.find((w) => w.id === id);
        if (widget) {
          result = [...result, widget];
        }
      }
    }
    return result;
  }

  getLSFavourites(): number[] {
    const favourites = localStorage.getItem(LOCAL_STORAGE_KEY);
    return favourites ? JSON.parse(favourites) : [];
  }

  async getFavourites(): Promise<number[]> {
    const url = `${APP_API_BASE_URL}/analytics/me/settings`;
    const favourites = await apiGet<number[]>(
      url,
      getUserSession().access_token
    );
    const lsFavourites = this.getLSFavourites();

    if (lsFavourites.length > 0 && !isEqualObject(favourites, lsFavourites)) {
      this.saveFavourites(lsFavourites);
      return lsFavourites;
    } else {
      if (lsFavourites.length === 0) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(favourites));
      }
      return favourites;
    }
  }

  async saveFavourites(widgets: number[]): Promise<{ widgets: number[] }> {
    const url = `${APP_API_BASE_URL}/analytics/me/settings`;

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(widgets));
    return apiPost(url, { widgets }, getUserSession().access_token);
  }

  isWidgetStorageEvent(e: StorageEvent): boolean {
    return e.key === LOCAL_STORAGE_KEY;
  }

  async getPeriods(): Promise<AnalyticsPeriodOptions> {
    const url = `${APP_API_BASE_URL}/analytics/periods`;
    return apiGet(url, getUserSession().access_token);
  }
}

const AnalyticsService = new AnalyticsServiceClass();

export default AnalyticsService;
